import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { projectDoc } from '../constants/firestorePaths';

/**
 * Live read of a project's opt-in `approvalWorkflowEnabled` flag.
 *
 * The governance review gate (Submit for review / Approve / Reject /
 * Request changes) is off by default — `enabled` stays `false` while the
 * project document is loading and whenever the flag is absent or not
 * exactly `true`, so the panel never flashes before we know the setting.
 */
export function useApprovalWorkflowEnabled(projectId: string | undefined): {
  enabled: boolean;
  loading: boolean;
} {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setEnabled(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      projectDoc(projectId),
      snap => {
        const data = snap.data() as
          | { approvalWorkflowEnabled?: boolean }
          | undefined;
        setEnabled(data?.approvalWorkflowEnabled === true);
        setLoading(false);
      },
      err => {
        console.warn('useApprovalWorkflowEnabled snapshot error:', err);
        setEnabled(false);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [projectId]);

  return { enabled, loading };
}
